import { computed, Injectable, signal } from "@angular/core";
import { environment } from "src/environments/environment";
import { parseOpsHttpUrl } from "../helpers/ops-http-inspector-format.helper";
import { OpsHttpInspectorEntry } from "../models/ops-http-inspector-entry.model";

const DEFAULT_HISTORY_LIMIT = 10;

/**
 * Tracks recent HTTP calls made by the app (bounded FIFO history, oldest
 * evicted first) so the Ops Console inspector bar can always show the last
 * request/response plus a short history. Limit configurable via
 * `environment.opsHttpInspectorHistoryLimit`. Populated by
 * `opsHttpInspectorInterceptor`.
 */
@Injectable({ providedIn: "root" })
export class OpsHttpInspectorService {
  private readonly historyLimit = Math.max(
    1,
    environment.opsHttpInspectorHistoryLimit ?? DEFAULT_HISTORY_LIMIT,
  );

  // Oldest first, newest last.
  private readonly _history = signal<OpsHttpInspectorEntry[]>([]);
  readonly history = this._history.asReadonly();

  readonly lastEntry = computed(() => {
    const entries = this._history();
    return entries.length ? entries[entries.length - 1] : null;
  });

  startEntry(
    method: string,
    url: string,
    requestHeaders: Record<string, string>,
    requestBody: unknown,
  ): string {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const { baseUrl, queryParams } = parseOpsHttpUrl(url);
    const entry: OpsHttpInspectorEntry = {
      id,
      method,
      url,
      baseUrl,
      queryParams,
      startedAt: Date.now(),
      finishedAt: null,
      durationMs: null,
      status: "pending",
      statusCode: null,
      statusText: "",
      requestHeaders,
      requestBody,
      responseHeaders: {},
      responseBody: null,
      errorMessage: null,
    };

    this._history.update((entries) => {
      const next = [...entries, entry];
      // FIFO: once over the limit, the oldest entry (index 0) is dropped.
      return next.length > this.historyLimit ? next.slice(next.length - this.historyLimit) : next;
    });

    return id;
  }

  completeEntry(id: string, patch: Partial<OpsHttpInspectorEntry>): void {
    this._history.update((entries) =>
      entries.map((entry) => {
        if (entry.id !== id) {
          return entry;
        }
        const finishedAt = Date.now();
        return { ...entry, ...patch, finishedAt, durationMs: finishedAt - entry.startedAt };
      }),
    );
  }
}
